import { clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs))
}

export function formatDate(date) {
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(new Date(date))
}

export function formatRelativeTime(date) {
  const now = new Date()
  const targetDate = new Date(date)
  const diffInSeconds = Math.floor((now - targetDate) / 1000)
  
  if (diffInSeconds < 60) {
    return "just now"
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60)
    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600)
    return `${hours} hour${hours > 1 ? 's' : ''} ago`
  } else if (diffInSeconds < 2592000) {
    const days = Math.floor(diffInSeconds / 86400)
    return `${days} day${days > 1 ? 's' : ''} ago`
  } else {
    return formatDate(date)
  }
}

export function getInitials(name) {
  return name
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export function getRatingColor(rating) {
  if (rating >= 4) return "text-green-600"
  if (rating >= 3) return "text-yellow-600"
  if (rating >= 2) return "text-orange-600"
  return "text-red-600"
}

export function truncateText(text, maxLength = 100) {
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength) + "..."
}

export function formatReviewDate(dateString) {
  if (!dateString) return 'Unknown date'
  
  // If it's a relative date string like "2 days ago", return as is
  if (typeof dateString === 'string' && dateString.includes('ago')) {
    return dateString
  }
  
  // Try to parse as ISO date or other date formats
  try {
    let date
    
    // Handle ISO date strings (new backend format)
    if (typeof dateString === 'string' && (dateString.includes('T') || dateString.match(/^\d{4}-\d{2}-\d{2}/))) {
      date = new Date(dateString)
    } else {
      date = new Date(dateString)
    }
    
    if (isNaN(date.getTime())) {
      return dateString // Return original string if not a valid date
    }
    
    // Format as relative time if recent, otherwise as formatted date
    const now = new Date()
    const diffInMs = now - date
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60))
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60))
    
    if (diffInMinutes < 1) {
      return 'Just now'
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`
    } else if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`
    } else if (diffInDays === 0) {
      return 'Today'
    } else if (diffInDays === 1) {
      return 'Yesterday'
    } else if (diffInDays < 7) {
      return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`
    } else if (diffInDays < 30) {
      const weeks = Math.floor(diffInDays / 7)
      return `${weeks} week${weeks > 1 ? 's' : ''} ago`
    } else if (diffInDays < 365) {
      const months = Math.floor(diffInDays / 30)
      return `${months} month${months > 1 ? 's' : ''} ago`
    } else {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    }
  } catch (error) {
    console.warn('Error formatting date:', dateString, error)
    return dateString // Return original string if parsing fails
  }
}

// Add utility function to parse review dates consistently
export function parseReviewDate(dateString) {
  if (!dateString) return null
  
  try {
    // Handle ISO date strings (new backend format)
    if (typeof dateString === 'string' && (dateString.includes('T') || dateString.match(/^\d{4}-\d{2}-\d{2}/))) {
      return new Date(dateString)
    }
    
    // Handle relative dates - convert to approximate dates
    if (typeof dateString === 'string' && dateString.includes('ago')) {
      const now = new Date()
      
      // Extract number and unit
      const match = dateString.match(/(\d+)\s*(minute|hour|day|week|month|year)s?\s*ago/i)
      if (match) {
        const value = parseInt(match[1])
        const unit = match[2].toLowerCase()
        
        switch (unit) {
          case 'minute':
            return new Date(now.getTime() - value * 60 * 1000)
          case 'hour':
            return new Date(now.getTime() - value * 60 * 60 * 1000)
          case 'day':
            return new Date(now.getTime() - value * 24 * 60 * 60 * 1000)
          case 'week':
            return new Date(now.getTime() - value * 7 * 24 * 60 * 60 * 1000)
          case 'month':
            return new Date(now.getTime() - value * 30 * 24 * 60 * 60 * 1000)
          case 'year':
            return new Date(now.getTime() - value * 365 * 24 * 60 * 60 * 1000)
        }
      }
      
      // Handle special cases
      if (dateString.includes('yesterday')) {
        return new Date(now.getTime() - 24 * 60 * 60 * 1000)
      }
      if (dateString.includes('today') || dateString.includes('just now')) {
        return now
      }
    }
    
    return new Date(dateString)
  } catch (error) {
    console.warn('Error parsing date:', dateString, error)
    return null
  }
}

// Add utility function to check if date is within period
export function isDateWithinPeriod(dateString, period = '30d') {
  const date = parseReviewDate(dateString)
  if (!date) return false
  
  const now = new Date()
  let periodStart
  
  switch (period) {
    case '7d':
      periodStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
      break
    case '30d':
      periodStart = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
      break
    case '60d':
      periodStart = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000)
      break
    case '90d':
      periodStart = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000)
      break
    case '1y':
      periodStart = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000)
      break
    default:
      periodStart = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
  }
  
  return date >= periodStart
}
